import type { FileKind } from './files/fileKind';

/**
 * The Icon and Glyph Reference (piece 1): one recurring meaning maps to exactly
 * one Lucide icon name, so the same concept always draws the same icon and no
 * component picks its own at a call site. The specification's Table 8 supplies
 * the meanings; this table authors the names. 🔒 A name starting `app-` is a
 * glyph the application draws itself where Lucide has none (decision 3011).
 */
export const iconReference = {
  /** The filled section marker; no Tile header draws it since decision 1327. Carries no status meaning. */
  statusDot: 'circle',
  /** An RSVP'd attendee, a checked box. */
  confirmed: 'check',
  /** A partly checked box. */
  indeterminate: 'minus',
  /** Read-only because the value is pulled from a linked record. */
  readOnlyLinked: 'lock',
  /** The lock beside a Redacted Value. */
  confidential: 'lock',
  /** Decision 7420: the lock beside the name of a file whose bytes are stored encrypted. */
  fileEncrypted: 'lock',
  /** Remove a chip, a line-item row, an attendee. */
  remove: 'x',
  /** The Title Bar control that closes a modal, which acts as Cancel (piece 5). */
  closeModal: 'x',
  /** Opens the edit modal or inline edit. */
  edit: 'pencil',
  /** Month navigation and pagination, backwards. */
  previous: 'chevron-left',
  /** Month navigation and pagination, forwards. */
  next: 'chevron-right',
  /** Navigate into a record from a list row. */
  navigateInto: 'chevron-right',
  /** Opens outside the application. */
  externalLink: 'external-link',
  /** A page on the web a row names, such as a school's LinkedIn page; it draws a globe, never the out glyph (decision 6460). */
  webPage: 'globe',
  /** Expand a group or summary row. */
  expand: 'chevron-down',
  /** Collapse a group or summary row. */
  collapse: 'chevron-up',
  /** An empty, actionable upload slot. */
  uploadSlot: 'file-plus',
  /** Amount in Books, the internal ledger balance. */
  amountInBooks: 'wallet',
  /** Amount in Bank Feed, the live external balance. */
  amountInBankFeed: 'landmark',
  settings: 'settings',
  /** Star or favourite. */
  star: 'star',
  warning: 'triangle-alert',
  fieldError: 'circle-alert',
  /** An Alert Banner's status icon before its words (decision 1326): a tick, a triangle, a circle-alert, an info mark. */
  bannerGood: 'check',
  bannerWarning: 'triangle-alert',
  bannerError: 'circle-alert',
  bannerInfo: 'info',
  /** Reveal a hidden value. */
  reveal: 'eye',
  /** Hide a revealed value. */
  hide: 'eye-off',
  /** The six-dot drag handle of the drag-and-drop list (piece 4). */
  dragHandle: 'grip-vertical',
  /** The keyboard's way of moving a row one place up or down in a reorderable list (piece 4; decision 0333). */
  moveUp: 'chevron-up',
  moveDown: 'chevron-down',
  /** A locked Tile in an edit context: fixed position and size. */
  locked: 'lock',
  /** An unlocked Tile in an edit context: movable, resizable, removable. */
  unlocked: 'lock-open',
  /** A file item in a Tile or a queued upload. */
  file: 'file',
  /** A Location item in a Tile, the place field of an event, and Open in Maps on an address row. */
  location: 'map-pin',
  /** Call the number a contact row carries. */
  callNumber: 'phone',
  /** Download, on a link whose permission allows it. */
  download: 'download',
  /** Print, on a link whose permission allows it. */
  print: 'printer',
  /** Retry a failed load or upload. */
  retry: 'refresh-cw',
  /** Add a row or an item. */
  add: 'plus',
  /** A field that searches: the Dropdown's own glyph beside its chevron. */
  search: 'search',
  /** The header control that opens the phone drawer (piece 12). */
  mainMenu: 'menu',
  /** The avatar menu's own glyph where no picture and no initials exist. */
  account: 'circle-user',
  /** Leaving the application. */
  signOut: 'log-out',
  /** The pinned landing page. */
  dashboard: 'layout-dashboard',
  /** A Company, in the navigation bar and wherever an account is named. */
  company: 'building-2',
  /** A Person, in the navigation bar and on a record row. */
  person: 'user',
  /** A Site: a company's occupancy of a building, which the Location is. */
  site: 'factory',
  /** An Item, the catalog entry, in the navigation bar (piece 17). */
  item: 'package',
  /** A Deal, the sales opportunity, in the navigation bar (piece 19). */
  deal: 'handshake',
  /** The Parts tile on New Deal (piece 18; T1876): the parts a customer asked for, the Item's own package. */
  dealParts: 'package',
  /** The Service tile on New Deal (piece 18; T1876): a service call, the Work Order's own wrench. */
  dealService: 'wrench',
  /** The Project tile on New Deal (piece 18; T1876): a priced scope built on site, a hard hat. */
  dealProject: 'hard-hat',
  /** A Lead, the claim before the relationship, in the navigation bar (piece 20). */
  lead: 'target',
  /** The Quote (piece 22): the document a customer receives. */
  quote: 'file-text',
  /** The Request for Quotation (piece 18): the sheet that goes out asking vendors a price. */
  rfq: 'file-question-mark',
  /** The bill of materials (piece 18): the parts a job is built from, a list that nests. */
  bom: 'list-tree',
  /** The Calendar, in the navigation bar. */
  calendar: 'calendar',
  /** The navigation bar's footer control, collapsing it to the rail. */
  collapsePanel: 'panel-left-close',
  /** The same control in the rail, opening the bar back up. */
  expandPanel: 'panel-left-open',
  /** A refusal the shell renders rather than redirecting away from. */
  accessDenied: 'shield-alert',
  /** The Work Order's own glyph, on its nav row and its banner (piece 22). */
  workOrder: 'wrench',
  /** The asset register's own glyph, on its nav row (piece 28): the equipment in the tree. */
  asset: 'boxes',
  /**
   * 🔒 Each asset level's own glyph, drawn on its level colour where a node has no
   * picture of its own or its catalog part's (decision 12562). Four glyphs that
   * mean nothing else in this table, so a level never reads as a Site or a tag.
   */
  assetLevelGroup: 'warehouse',
  assetLevelFunction: 'workflow',
  assetLevelComponentGroup: 'component',
  assetLevelComponent: 'cog',
  /** The dispatch board's own glyph, on its nav row (piece 29): who can get there fastest. */
  dispatch: 'route',
  /** My Jobs' own glyph, on its nav row (piece 29; decision 1909): the technician's own board. */
  myJobs: 'hard-hat',
  /** The Service Reports list's own glyph, on its nav row (piece 29; decision 2910): the visit's signed sheet. */
  serviceReport: 'clipboard-check',
  /** The Schedule view's own glyph, on its nav row and its banner (piece 29; decision 2081): technicians against the day. */
  schedule: 'calendar-range',
  /** The Time Off Requests row's own glyph on the HR group (piece 12; decision 13250): leave a manager decides. */
  timeOff: 'calendar-check',
  /** The on-call roster's own glyph, on its nav row and its banner (piece 29; decision 4000): who answers the phone after hours. */
  onCall: 'phone-call',
  /** Decision 11860: the phone system's three nav rows and their banners: Calls (Recents), Voicemail and Texts. */
  phoneCalls: 'history',
  voicemail: 'voicemail',
  phoneTexts: 'message-square',
  /** Decision 11860: a call's direction in the Calls list, drawn with its word for the screen reader. */
  callInbound: 'phone-incoming',
  callOutbound: 'phone-outgoing',
  /** Decision 11860: Play on a call's recording. */
  playRecording: 'play',
  /** The Agreements' own glyph, on its nav row (piece 29): the standing contract. */
  agreement: 'scroll-text',
  /** Emergency intake's own glyph, on its nav row (piece 29). */
  emergency: 'siren',
  /** The Knowledge Base's own glyph, on its nav row and its banner (piece 30). */
  knowledgeBase: 'book-open',
  /** Insert Table of Contents, the article editor's own tool (piece 30; decision 0750). */
  tableOfContents: 'table-of-contents',
  /** A training article's Required Quiz, on its tile and its page (piece 30). */
  quiz: 'graduation-cap',
  /** The connectivity-lost banner. */
  offline: 'wifi-off',
  // The Settings Index's category tiles, one glyph per category beside its title (piece 15; decision 0289).
  /** Settings › People: departments, roles and the people themselves. */
  people: 'users',
  /** Settings › CRM: tags, custom fields and the sales rules. */
  crm: 'contact',
  /** Settings › Documents: file types, release and the document classes. */
  documents: 'folder',
  /** Settings › Security: the permission model and its disclosures. */
  security: 'shield-check',
  /** Settings › Catalog: categories, attribute types and pricing defaults. */
  catalog: 'library',
  /** Settings › Terms & Conditions: the term containers and the signature block. */
  terms: 'file-signature',
  /** Settings › Accounting: the ledger's own configuration. */
  accounting: 'receipt',
  /** Settings › HR: retention, the workweek and the holidays. */
  hr: 'briefcase',
  /** Settings › Email: the legal footer and the templates. */
  mail: 'mail',
  /** Settings › Notifications: the reminder rules. */
  notification: 'bell',
  /** Settings › Mobile: the device registry. */
  mobile: 'smartphone',
  /** Settings › Modules: the switches that turn a module on. */
  modules: 'blocks',
  /** The Rich Text Editor's toolbar (piece 5; specification figure 50): one glyph per command. */
  heading: 'heading',
  bold: 'bold',
  italic: 'italic',
  underline: 'underline',
  strikethrough: 'strikethrough',
  bulletList: 'list',
  numberedList: 'list-ordered',
  alignLeft: 'align-left',
  alignCenter: 'align-center',
  alignRight: 'align-right',
  alignJustify: 'align-justify',
  image: 'image',
  textColor: 'baseline',
  highlight: 'highlighter',
  /** The article editor's word-processor set (piece 30; decision 0800). */
  indent: 'indent-increase',
  outdent: 'indent-decrease',
  link: 'link',
  horizontalRule: 'separator-horizontal',
  table: 'table',
  tableRowAdd: 'between-horizontal-end',
  tableRowRemove: 'fold-vertical',
  tableColumnAdd: 'between-vertical-end',
  tableColumnRemove: 'fold-horizontal',
  tableHeader: 'panel-top',
  /** Insert Placeholder, the merge-field chip. */
  mergeField: 'braces',
  /** The Duplicates report's nav row (piece 10; decision 1484): two records that look like one. */
  duplicates: 'copy',
  /** A vendor's invoice, the payable side of an order (piece 22). */
  bill: 'receipt',
  /** A credit a vendor has granted us, money going back the other way. */
  vendorCredit: 'undo-2',
  /** A credit we have granted a customer, the sell-side mirror of `vendorCredit` (piece 22). */
  creditNote: 'file-minus',
  /** Money received or sent against a document, on its own list and its own page (piece 22). */
  payment: 'hand-coins',
  /** Money we paid out, the Payments made row beside Payments received (decision 0762). */
  paymentMade: 'banknote-arrow-up',
  /** A physical count of a site's shelves. */
  stockCount: 'clipboard-list',
  /** Stock moving from one site to another. */
  stockTransfer: 'truck',
  /** A change in the value of stock on hand, at an unchanged quantity. */
  stockRevaluation: 'scale',
  /** Sales tax: the rate table, the registrations and the returns (piece 25). */
  salesTax: 'percent',
  /** Piece 24: the bank and card accounts money moves through. */
  bankAccount: 'building-2',
  /** Piece 24: one payment run against as many bills as it covers. */
  payBills: 'banknote',
  /** Piece 24: the checks waiting to be numbered at print. */
  checks: 'square-pen',
  /** Piece 24: an employee's own mileage and expense report. */
  expenseReport: 'receipt',
  /** The Help and About control beside a page's title (piece 5, decision 0443). */
  help: 'circle-help',
  /** A note's replies, the small speech bubble with its count that opens them (piece 4; decision 5493). */
  comment: 'message-circle',
  /** Reply to the sender, the curved arrow every mail client draws in a message's header (piece 32; T345). */
  reply: 'reply',
  /** Reply to everybody on the message, the doubled curved arrow. */
  replyAll: 'reply-all',
  /** Forward the message to somebody new, the straight arrow. */
  forward: 'forward',
  /** A received message's attachment card, one glyph per kind of file (piece 32; T347): a PDF. */
  attachmentPdf: 'file-text',
  /** A picture. */
  attachmentImage: 'file-image',
  /** A spreadsheet or a CSV. */
  attachmentSpreadsheet: 'file-spreadsheet',
  /** A word-processor document or plain text. */
  attachmentDocument: 'file-type',
  /** A zip or another archive. */
  attachmentArchive: 'file-archive',
  /** Any other file. */
  attachmentOther: 'file',
  /** The collapsed mail folder rail (piece 32; decision 1474): a mailbox's own row, then one glyph per folder kind. */
  mailbox: 'mailbox',
  mailInbox: 'inbox',
  mailDrafts: 'file-pen',
  mailSent: 'send',
  mailOutbox: 'clock-arrow-up',
  mailArchive: 'archive',
  mailQuarantine: 'shield-x',
  mailTrash: 'trash-2',
  /** A person's own folder. */
  mailFolder: 'folder',
  /** The mail list's row marks (decision 1478): a message carries an attachment, is flagged, or is high priority. */
  mailAttachment: 'paperclip',
  mailFlag: 'flag',
  mailHighPriority: 'arrow-up',
  /** T346 (decision 1515): a message in the accounting mailbox filed against the Bill or the Payment it made. */
  mailDealtWith: 'check',
  /** The mail list's row actions on hover (decision 1478): mark read, mark unread and restore from Trash; Archive and Delete draw their folders' glyphs. */
  mailMarkRead: 'mail-open',
  mailMarkUnread: 'mail',
  mailRestore: 'undo-2',
  /** One control that opens a menu of the actions folded behind it, as a mail row's are where four do not fit beside its middle. */
  moreActions: 'ellipsis',
  /** 🔒 The phone's sheet zoom, one step out and one step in (T2190; decision 3051): the glyphs `indeterminate` and `add` draw, as acts of their own. */
  zoomOut: 'minus',
  zoomIn: 'plus',
  /** 🔒 Delete this link, the holder's correction of a link made by mistake (T728; decision 1500). */
  deleteLink: 'trash-2',
  /** 🔒 A Tax Exempt certificate, the rosette on the gold badge of a site that holds one (T3076; decision 7390). Shares the glyph the Award tag category draws, which decision 7390 amends 2260 to allow. */
  taxExemptCertificate: 'award',
  /**
   * 🔒 The six report category rows' own glyphs, each a chart, so the collapsed
   * rail reads them as reports and no two rows share one (piece 26; T853).
   */
  reportFinancial: 'file-chart-pie',
  reportSales: 'chart-line',
  reportPurchases: 'file-chart-column',
  reportInventory: 'chart-bar-stacked',
  reportTax: 'file-chart-line',
  reportPipeline: 'trending-up',
  /**
   * 🔒 A tag category's icon (decision 2260): sixteen glyphs that mean nothing
   * else in this table, so a category pill never reads as a Site, a Location or
   * a Work order. `tag` also draws a tag that has no category.
   */
  tag: 'tag',
  tagCategoryAward: 'award',
  tagCategoryLayers: 'layers',
  tagCategoryCpu: 'cpu',
  tagCategoryMap: 'map',
  tagCategoryMegaphone: 'megaphone',
  tagCategoryGauge: 'gauge',
  tagCategoryAlarmClock: 'alarm-clock',
  tagCategoryTag: 'tag',
  tagCategoryZap: 'zap',
  tagCategoryGlobe: 'globe',
  tagCategoryThermometer: 'thermometer',
  tagCategoryFlame: 'flame',
  tagCategoryLeaf: 'leaf',
  tagCategoryGem: 'gem',
  tagCategoryBookmark: 'bookmark',
  tagCategoryCompass: 'compass',
  /**
   * 🔒 A file row's own icon, one per kind of file (decision 3011; the owner's
   * set of 2026-10-11): a white page with a folded corner, a glyph in its middle
   * and a black band reading the kind. No Lucide icon draws them, so they are
   * the application's own glyphs under `app-` names, drawn through this one
   * control all the same.
   */
  fileKindPdf: 'app-file-pdf',
  fileKindDoc: 'app-file-doc',
  fileKindXls: 'app-file-xls',
  fileKindCsv: 'app-file-csv',
  fileKindTxt: 'app-file-txt',
  fileKindJpg: 'app-file-jpg',
  fileKindPng: 'app-file-png',
  fileKindGif: 'app-file-gif',
  fileKindSvg: 'app-file-svg',
  fileKindZip: 'app-file-zip',
  fileKindDwg: 'app-file-dwg',
  fileKindDxf: 'app-file-dxf',
  fileKindDgn: 'app-file-dgn',
  fileKindPpt: 'app-file-ppt',
  fileKindJson: 'app-file-json',
  fileKindXml: 'app-file-xml',
  fileKindHtml: 'app-file-html',
  fileKindMp3: 'app-file-mp3',
  fileKindMp4: 'app-file-mp4',
  /** Any other kind of file, its band reading ETC. */
  fileKindEtc: 'app-file-etc',
  /** 🔒 Share a file, the narrow file row's small icon button beside its name (decision 3013). */
  share: 'share-2',
  /** 🔒 A clean scan, beneath a file row's icon (decision 3012): a green shield with a white check. */
  scanClean: 'app-scan-clean',
} as const;

export type IconMeaning = keyof typeof iconReference;
export type LucideIconName = Exclude<(typeof iconReference)[IconMeaning], `app-${string}`>;
/** 🔒 A glyph the application draws itself (decision 3011). */
export type AppGlyphName = Extract<(typeof iconReference)[IconMeaning], `app-${string}`>;
/** Every name the reference resolves to: a Lucide icon or one of the application's own glyphs. */
export type IconName = (typeof iconReference)[IconMeaning];

/** 🔒 The meaning each file kind's icon draws under (decision 3011). */
export const FILE_KIND_ICON = {
  pdf: 'fileKindPdf', doc: 'fileKindDoc', xls: 'fileKindXls', csv: 'fileKindCsv', txt: 'fileKindTxt', jpg: 'fileKindJpg', png: 'fileKindPng', gif: 'fileKindGif', svg: 'fileKindSvg', zip: 'fileKindZip',
  dwg: 'fileKindDwg', dxf: 'fileKindDxf', dgn: 'fileKindDgn', ppt: 'fileKindPpt', json: 'fileKindJson', xml: 'fileKindXml', html: 'fileKindHtml', mp3: 'fileKindMp3', mp4: 'fileKindMp4', etc: 'fileKindEtc',
} as const satisfies Record<FileKind, IconMeaning>;

/** Meanings drawn as a filled glyph rather than an outline. */
export const filledMeanings: ReadonlySet<IconMeaning> = new Set<IconMeaning>(['statusDot']);

/** Stroke width tracks icon size at 1/12, Lucide's own 2px-at-24px default; it is never set per icon. */
export const ICON_STROKE_RATIO = 1 / 12;

export function strokeWidthFor(sizePx: number): number {
  return sizePx * ICON_STROKE_RATIO;
}
